import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';

export interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  delay?: number;
  maxWidth?: string;
  disabled?: boolean;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  position = 'auto',
  delay = 300,
  maxWidth = '320px',
  disabled = false,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const { darkMode } = useTheme();
  
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef(false);
  const isTooltipHoveringRef = useRef(false);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Calculate optimal position
  const calculatePosition = useCallback(() => {
    if (!containerRef.current || position !== 'auto') {
      setActualPosition(position as 'top' | 'bottom' | 'left' | 'right');
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    const spaceTop = rect.top;
    const spaceBottom = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;
    
    if (spaceTop > 100) {
      setActualPosition('top');
    } else if (spaceBottom > 100) {
      setActualPosition('bottom');
    } else if (spaceRight > 200) {
      setActualPosition('right');
    } else if (spaceLeft > 200) {
      setActualPosition('left');
    } else {
      const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
      if (maxSpace === spaceTop) setActualPosition('top');
      else if (maxSpace === spaceBottom) setActualPosition('bottom');
      else if (maxSpace === spaceRight) setActualPosition('right');
      else setActualPosition('left');
    }
  }, [position]);

  // Calculate tooltip position
  const calculateTooltipPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    let x = rect.left + rect.width / 2;
    let y = rect.top;
    
    switch (actualPosition) {
      case 'top':
        y = rect.top - 8;
        break;
      case 'bottom':
        y = rect.bottom + 8;
        break;
      case 'left':
        x = rect.left - 8;
        y = rect.top + rect.height / 2;
        break;
      case 'right':
        x = rect.right + 8;
        y = rect.top + rect.height / 2;
        break;
    }
    
    setTooltipPosition({ x, y });
  }, [actualPosition]);

  // Show tooltip
  const showTooltip = useCallback(() => {
    if (disabled || !content) return;
    
    clearTimeouts();
    calculatePosition();
    
    showTimeoutRef.current = setTimeout(() => {
      calculateTooltipPosition();
      setIsVisible(true);
    }, delay);
  }, [disabled, content, delay, calculatePosition, calculateTooltipPosition, clearTimeouts]);

  // Hide tooltip
  const hideTooltip = useCallback(() => {
    clearTimeouts();
    
    hideTimeoutRef.current = setTimeout(() => {
      // Only hide if neither container nor tooltip is being hovered
      if (!isHoveringRef.current && !isTooltipHoveringRef.current) {
        setIsVisible(false);
      }
    }, 100);
  }, [clearTimeouts]);

  // Container mouse events
  const handleContainerMouseEnter = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    isHoveringRef.current = true;
    showTooltip();
  }, [showTooltip]);

  const handleContainerMouseLeave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    isHoveringRef.current = false;
    hideTooltip();
  }, [hideTooltip]);

  // Tooltip mouse events
  const handleTooltipMouseEnter = useCallback(() => {
    isTooltipHoveringRef.current = true;
    clearTimeouts();
  }, [clearTimeouts]);

  const handleTooltipMouseLeave = useCallback(() => {
    isTooltipHoveringRef.current = false;
    hideTooltip();
  }, [hideTooltip]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isVisible) return;

    const updatePosition = () => {
      calculateTooltipPosition();
    };

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible, calculateTooltipPosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  // Hide tooltip when component unmounts or content changes
  useEffect(() => {
    return () => {
      setIsVisible(false);
    };
  }, [content]);

  const getPortalPositionClasses = () => {
    const baseClasses = 'fixed z-[9999] pointer-events-auto';
    
    switch (actualPosition) {
      case 'top':
        return `${baseClasses} transform -translate-x-1/2 -translate-y-full`;
      case 'bottom':
        return `${baseClasses} transform -translate-x-1/2`;
      case 'left':
        return `${baseClasses} transform -translate-x-full -translate-y-1/2`;
      case 'right':
        return `${baseClasses} transform -translate-y-1/2`;
      default:
        return `${baseClasses} transform -translate-x-1/2 -translate-y-full`;
    }
  };

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-0 h-0 border-4';
    const borderColor = darkMode ? 'border-gray-800' : 'border-gray-900';
    
    switch (actualPosition) {
      case 'top':
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent ${borderColor}`;
      case 'bottom':
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent ${borderColor}`;
      case 'left':
        return `${baseClasses} left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent ${borderColor}`;
      case 'right':
        return `${baseClasses} right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent ${borderColor}`;
      default:
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent ${borderColor}`;
    }
  };

  if (disabled || !content) {
    return <>{children}</>;
  }

  const tooltipContent = isVisible && (
    <div
      ref={tooltipRef}
      className={getPortalPositionClasses()}
      style={{ 
        maxWidth,
        left: tooltipPosition.x,
        top: tooltipPosition.y,
        transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out'
      }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      <div className={`
        px-3 py-2 text-sm font-medium text-white rounded-lg shadow-xl
        ${darkMode 
          ? 'bg-gray-800 border border-gray-600' 
          : 'bg-gray-900 border border-gray-700'
        }
        whitespace-pre-line leading-relaxed
        backdrop-blur-sm
        relative
        animate-in fade-in-0 zoom-in-95 duration-200
      `}>
        {content}
        <div className={getArrowClasses()} />
      </div>
    </div>
  );

  return (
    <div 
      ref={containerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={handleContainerMouseEnter}
      onMouseLeave={handleContainerMouseLeave}
    >
      {children}
      
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </div>
  );
};

export default Tooltip;
